// ---------------------------------------------------------------------------
// Mounted Brand Kit settings — persisted brand contract only
// ---------------------------------------------------------------------------
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { ArrowRight, Check, Palette, Save, Sparkles } from "lucide-react";
import {
  normalizeSiteTheme,
  type BrandColors,
  type BrandFonts,
  type SiteTheme,
} from "@ease/page-builder";
import {
  classifyContrast,
  contrastRatio,
  WCAG_THRESHOLDS,
  type WcagLevel,
} from "@ease/ui";
import { Link } from "react-router";
import { apiFetch, ApiError } from "@/lib/api-client";
import { BRAND_CONTRACT_PATH } from "@/lib/api-paths";
import { normalizeColorInputValue } from "@/components/pages/dss/color-input-value";
import {
  buildPreviewTheme,
  resolveBrandInheritance,
  summarizeThemeInheritance,
} from "@/hooks/use-site-theme";

interface BrandKit {
  id?: string;
  logoUrl: string | null;
  iconUrl: string | null;
  colors: BrandColors;
  fonts: BrandFonts;
  brandVoice: string | null;
  tagline: string | null;
  businessName: string | null;
  industry: string | null;
  targetAudience: string | null;
}

interface SiteInheritanceRecord {
  id: string;
  name: string;
  slug: string;
  globalTheme: SiteTheme;
}

interface SiteInheritanceStatus {
  site: SiteInheritanceRecord;
  brandDefaultCount: number;
  siteOverrideCount: number;
  overriddenFields: string[];
}

const DEFAULT_COLORS = {
  primary: "#1040CC",
  secondary: "#7C3AED",
  accent: "#F05E0A",
  background: "#FFFFFF",
  text: "#0F172A",
};

const DEFAULT_FONTS: BrandFonts = {
  headings: { family: "Inter", weight: "700" },
  body: { family: "Inter", weight: "400" },
};

const DEFAULT_THEME_BASELINE = normalizeSiteTheme({ presetId: "modern" });
const DEFAULT_THEME_COLORS = DEFAULT_THEME_BASELINE.colors ?? {};
const DEFAULT_PREVIEW_SURFACE = DEFAULT_THEME_COLORS.surface ?? "#F8FAFC";
const DEFAULT_PREVIEW_MUTED = DEFAULT_THEME_COLORS.muted ?? "#E2E8F0";
const DEFAULT_PREVIEW_RADIUS = DEFAULT_THEME_BASELINE.borderRadius ?? "12px";

const VOICE_OPTIONS = [
  { label: "Professional", value: "professional" },
  { label: "Casual", value: "casual" },
  { label: "Friendly", value: "friendly" },
  { label: "Authoritative", value: "authoritative" },
  { label: "Playful", value: "playful" },
] as const;

export default function BrandKitPage() {
  const [brand, setBrand] = useState<BrandKit>({
    logoUrl: null,
    iconUrl: null,
    colors: DEFAULT_COLORS,
    fonts: DEFAULT_FONTS,
    brandVoice: null,
    tagline: null,
    businessName: null,
    industry: null,
    targetAudience: null,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [siteStatuses, setSiteStatuses] = useState<SiteInheritanceStatus[]>([]);

  const previewTheme = useMemo(
    () => buildPreviewTheme(DEFAULT_THEME_BASELINE, {
      colors: brand.colors,
      fonts: brand.fonts,
    }),
    [brand.colors, brand.fonts],
  );
  const previewStyle = useMemo(() => buildPreviewStyle(previewTheme), [previewTheme]);
  const propagationNotes = useMemo(
    () => [
      {
        title: "Inherited by default",
        body: "Primary, accent, background, text, and heading/body fonts flow into every site theme until a site saves an explicit override.",
      },
      {
        title: "Truthful white-labeling",
        body: "Brand inputs become native product styling defaults, so customer-facing surfaces feel owned by the business instead of decorated after the fact.",
      },
      {
        title: "Next approval seam",
        body: "After saving here, review a specific site in site settings and clear any field that should keep inheriting this shared brand truth.",
      },
    ],
    [],
  );

  const computeSiteStatuses = useCallback(
    (sites: SiteInheritanceRecord[], brandKit: BrandKit) => {
      const statuses = sites.map((site) => {
        const resolved = resolveBrandInheritance(
          site.globalTheme ?? {},
          { colors: brandKit.colors, fonts: brandKit.fonts },
        );
        const summary = summarizeThemeInheritance(resolved.inheritance);
        return {
          site,
          brandDefaultCount: summary.brandDefaultCount,
          siteOverrideCount: summary.siteOverrideCount,
          overriddenFields: summary.overriddenFields,
        };
      });
      setSiteStatuses(statuses);
    },
    [],
  );

  const fetchBrand = useCallback(async () => {
    setLoadError(null);

    try {
      const [brandResponse, sitesResponse] = await Promise.allSettled([
        apiFetch<{ data: BrandKit | null }>(BRAND_CONTRACT_PATH),
        apiFetch<{ data: SiteInheritanceRecord[] }>("/crm/pages/sites"),
      ]);

      if (brandResponse.status === "rejected") {
        const reason = brandResponse.reason;
        setLoadError(
          reason instanceof ApiError
            ? reason.message
            : "Failed to load brand kit. Showing local defaults until the mounted contract recovers.",
        );
      }

      const persistedBrand =
        brandResponse.status === "fulfilled" ? brandResponse.value.data : null;
      const sites =
        sitesResponse.status === "fulfilled" ? sitesResponse.value.data ?? [] : [];

      if (persistedBrand) {
        const mergedBrand: BrandKit = {
          ...brand,
          ...persistedBrand,
          colors: { ...DEFAULT_COLORS, ...(persistedBrand.colors ?? {}) },
          fonts: {
            headings: persistedBrand.fonts?.headings ?? DEFAULT_FONTS.headings,
            body: persistedBrand.fonts?.body ?? DEFAULT_FONTS.body,
          },
        };
        setBrand(mergedBrand);
        computeSiteStatuses(sites, mergedBrand);
      } else {
        computeSiteStatuses(sites, brand);
      }
    } catch (error) {
      setLoadError(
        error instanceof ApiError
          ? error.message
          : "Failed to load brand kit. Showing local defaults until the mounted contract recovers.",
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computeSiteStatuses]);

  useEffect(() => {
    void fetchBrand();
  }, [fetchBrand]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage("");

    try {
      await apiFetch(BRAND_CONTRACT_PATH, {
        method: "PUT",
        body: JSON.stringify({
          logoUrl: brand.logoUrl,
          iconUrl: brand.iconUrl,
          colors: brand.colors,
          fonts: brand.fonts,
          businessName: brand.businessName,
          industry: brand.industry,
          brandVoice: brand.brandVoice,
          targetAudience: brand.targetAudience,
          tagline: brand.tagline,
        }),
      });

      const sitesWithOverrides = siteStatuses.filter((s) => s.siteOverrideCount > 0);
      const inheritingCount = siteStatuses.length - sitesWithOverrides.length;
      const message = siteStatuses.length > 0
        ? `Brand kit saved. ${inheritingCount} of ${siteStatuses.length} site${siteStatuses.length === 1 ? "" : "s"} will inherit the updated values.`
        : "Brand kit saved.";
      setSaveMessage(message);
      setTimeout(() => setSaveMessage(""), 5000);

      // Recompute inheritance for current sites with new brand values
      computeSiteStatuses(
        siteStatuses.map((s) => s.site),
        brand,
      );
    } catch (error) {
      setSaveMessage(error instanceof ApiError ? error.message : "Save failed");
    } finally {
      setIsSaving(false);
    }
  };

  const updateColor = (key: keyof typeof DEFAULT_COLORS, value: string) => {
    setBrand((prev) => ({ ...prev, colors: { ...prev.colors, [key]: value } }));
  };

  const updateFont = (key: keyof BrandFonts, value: string) => {
    setBrand((prev) => ({
      ...prev,
      fonts: {
        ...prev.fonts,
        [key]: value.trim().length > 0
          ? {
              family: value,
              weight: key === "headings"
                ? prev.fonts.headings?.weight ?? "700"
                : prev.fonts.body?.weight ?? "400",
            }
          : undefined,
      },
    }));
  };

  return (
    <div className="space-y-8">
      <section className="rounded-[28px] border border-border bg-gradient-to-br from-primary/15 via-background to-background p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-muted-foreground">
              Mounted Brand Truth
            </p>
            <h1 className="text-2xl font-bold text-foreground">
              Set the brand kit that downstream site themes inherit by default.
            </h1>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              This is the canonical workspace brand contract. Save colors, fonts, logo, and voice
              here first; site settings then decide where a specific site keeps inheriting versus
              where it intentionally diverges.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/pages/site-settings"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
            >
              <Palette className="h-4 w-4" />
              Review site inheritance
            </Link>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
            >
              {isSaving ? <Save className="h-4 w-4" /> : <Check className="h-4 w-4" />}
              {isSaving ? "Saving..." : "Save Brand Kit"}
            </button>
          </div>
        </div>
      </section>

      {loadError ? (
        <div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-2 text-sm text-destructive">
          {loadError}
        </div>
      ) : null}

      {saveMessage ? <div className="rounded-md bg-accent px-4 py-2 text-sm">{saveMessage}</div> : null}

      <div className="grid gap-6 xl:grid-cols-[1.02fr_0.98fr]">
        <div className="space-y-6">
          <section className="rounded-[24px] border border-primary/25 bg-primary/5 px-5 py-4 text-sm text-foreground">
            <p className="font-medium">Brand values flow into site themes as defaults</p>
            <p className="mt-1 leading-6">
              Colors and fonts you set here become the inherited defaults for every site theme.
              Individual sites can override specific values in their{" "}
              <Link
                to="/pages/site-settings"
                className="font-medium underline underline-offset-2"
              >
                site settings
              </Link>
              . When a site does not override a value, changes here propagate automatically.
            </p>
          </section>

          <section className="space-y-4 rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <h2 className="text-lg font-medium text-foreground">Logo</h2>
            <p className="text-sm text-muted-foreground">
              Provide hosted logo and icon URLs directly. These are saved to the brand contract and
              displayed across all sites that reference this brand kit.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {(["logoUrl", "iconUrl"] as const).map((field) => (
                <div key={field} className="space-y-3 rounded-2xl border border-border bg-background p-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {field === "logoUrl" ? "Primary Logo URL" : "Icon URL"}
                    </p>
                    <input
                      type="url"
                      value={brand[field] ?? ""}
                      onChange={(event) => {
                        setBrand((prev) => ({
                          ...prev,
                          [field]: event.target.value.trim() || null,
                        }));
                      }}
                      placeholder="https://cdn.example.com/brand/logo.png"
                      className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                    />
                  </div>
                  {brand[field] ? (
                    <img src={brand[field] ?? ""} alt={field} className="h-16 rounded object-contain" />
                  ) : (
                    <div className="flex h-16 items-center justify-center rounded-xl bg-muted text-sm text-muted-foreground">
                      No image URL set
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-4 rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <h2 className="text-lg font-medium text-foreground">Color Palette</h2>
            <p className="text-sm text-muted-foreground">
              These colors become the inherited defaults for site themes. A site only stops
              inheriting a specific color when it saves an explicit override for that token.
            </p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
              {(Object.keys(DEFAULT_COLORS) as Array<keyof typeof DEFAULT_COLORS>).map((key) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs font-medium capitalize text-muted-foreground">
                    {key}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={normalizeColorInputValue(brand.colors[key])}
                      onChange={(event) => updateColor(key, event.target.value)}
                      className="h-8 w-8 cursor-pointer rounded border-0"
                    />
                    <input
                      type="text"
                      value={brand.colors[key]}
                      onChange={(event) => updateColor(key, event.target.value)}
                      className="w-full rounded-xl border border-border bg-background px-2 py-1 text-xs font-mono"
                    />
                  </div>
                  <ContrastAdvisory
                    swatch={brand.colors[key] ?? DEFAULT_COLORS[key]}
                    background={brand.colors.background ?? DEFAULT_COLORS.background}
                    label={key}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs leading-5 text-muted-foreground">
              Contrast hints check each color against your background.
              WCAG AA requires 4.5:1 for normal text. These are advisory — they
              never block saving.
            </p>
          </section>

          <section className="space-y-4 rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <h2 className="text-lg font-medium text-foreground">Typography</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {(["headings", "body"] as const).map((key) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs font-medium capitalize text-muted-foreground">
                    {key} Font
                  </label>
                  <input
                    type="text"
                    value={brand.fonts[key]?.family ?? ""}
                    onChange={(event) => updateFont(key, event.target.value)}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                  />
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-4 rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <h2 className="text-lg font-medium text-foreground">Voice & Tone</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input
                type="text"
                value={brand.businessName ?? ""}
                onChange={(event) => {
                  setBrand((prev) => ({ ...prev, businessName: event.target.value || null }));
                }}
                placeholder="Business name"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
              />
              <input
                type="text"
                value={brand.industry ?? ""}
                onChange={(event) => {
                  setBrand((prev) => ({ ...prev, industry: event.target.value || null }));
                }}
                placeholder="Industry"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
              />
              <textarea
                value={brand.targetAudience ?? ""}
                onChange={(event) => {
                  setBrand((prev) => ({ ...prev, targetAudience: event.target.value || null }));
                }}
                placeholder="Target audience"
                rows={3}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm sm:col-span-2"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {VOICE_OPTIONS.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => setBrand((prev) => ({ ...prev, brandVoice: value }))}
                  className={`rounded-full px-4 py-1.5 text-sm ${
                    brand.brandVoice === value
                      ? "bg-primary text-primary-foreground"
                      : "border border-border text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={brand.tagline ?? ""}
              onChange={(event) => {
                setBrand((prev) => ({ ...prev, tagline: event.target.value || null }));
              }}
              placeholder="Your brand tagline"
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
            />
          </section>
        </div>

        <div className="space-y-6">
          <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                  Theme Impact
                </p>
                <h2 className="mt-2 text-lg font-semibold text-foreground">
                  How this brand kit lands in a default site theme
                </h2>
              </div>
              <span className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" />
                Default preset: modern
              </span>
            </div>

            <div className="mt-5 overflow-hidden rounded-[24px] border border-black/10" style={previewStyle}>
              <div
                className="border-b px-5 py-4"
                style={{ borderColor: "var(--preview-border)", background: "var(--preview-surface)" }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {brand.logoUrl ? (
                      <img src={brand.logoUrl} alt="" className="h-10 w-10 rounded-xl object-cover" />
                    ) : (
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-xl text-xs font-semibold text-white"
                        style={{ background: "var(--preview-brand)" }}
                      >
                        {(brand.businessName ?? "B").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-foreground">{brand.businessName ?? "Your business"}</p>
                      <p className="text-xs text-muted-foreground">
                        {brand.tagline ?? "Brand defaults flowing into the published site."}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rounded-full px-4 py-2 text-sm font-semibold"
                    style={buildPreviewButtonStyle(previewTheme, false)}
                  >
                    Contact
                  </button>
                </div>
              </div>

              <div className="px-5 py-8" style={{ background: "var(--preview-bg)" }}>
                <div className="max-w-xl space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--preview-accent)" }}>
                    Brand-driven site preview
                  </p>
                  <h3
                    className="text-3xl font-semibold"
                    style={{
                      color: "var(--preview-text)",
                      fontFamily: buildPreviewFont(previewTheme.fonts?.heading?.family),
                    }}
                  >
                    {brand.tagline || "Your saved brand kit becomes the first truthful site theme."}
                  </h3>
                  <p
                    className="max-w-lg text-sm leading-6"
                    style={{
                      color: "var(--preview-text)",
                      opacity: 0.78,
                      fontFamily: buildPreviewFont(previewTheme.fonts?.body?.family),
                    }}
                  >
                    This preview uses the same inheritance order the platform applies elsewhere:
                    preset baseline, then brand defaults, then site-level overrides if a site opts
                    out of inheritance.
                  </p>
                  <div className="flex flex-wrap gap-3 pt-2">
                    <button
                      type="button"
                      className="rounded-full px-5 py-3 text-sm font-semibold"
                      style={buildPreviewButtonStyle(previewTheme, true)}
                    >
                      Primary CTA
                    </button>
                    <button
                      type="button"
                      className="rounded-full px-5 py-3 text-sm font-semibold"
                      style={buildPreviewButtonStyle(previewTheme, false)}
                    >
                      Secondary CTA
                    </button>
                  </div>
                </div>

                <div className="mt-8 grid gap-4 md:grid-cols-3">
                  {propagationNotes.map((card) => (
                    <div key={card.title} className="px-4 py-4" style={buildPreviewCardStyle(previewTheme)}>
                      <p
                        className="text-base font-semibold"
                        style={{
                          color: "var(--preview-text)",
                          fontFamily: buildPreviewFont(previewTheme.fonts?.heading?.family),
                        }}
                      >
                        {card.title}
                      </p>
                      <p
                        className="mt-2 text-sm leading-6"
                        style={{
                          color: "var(--preview-text)",
                          opacity: 0.74,
                          fontFamily: buildPreviewFont(previewTheme.fonts?.body?.family),
                        }}
                      >
                        {card.body}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <SiteInheritancePanel siteStatuses={siteStatuses} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Site Inheritance Panel — live status of how brand propagates to sites
// ---------------------------------------------------------------------------

function SiteInheritancePanel({ siteStatuses }: { siteStatuses: SiteInheritanceStatus[] }) {
  if (siteStatuses.length === 0) {
    return (
      <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-foreground">Site inheritance</h2>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          No sites created yet. Once you create a site, its theme will inherit these brand
          defaults automatically.
        </p>
        <Link
          to="/pages/site-settings"
          className="mt-4 inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
        >
          <Palette className="h-4 w-4" />
          Create your first site
        </Link>
      </section>
    );
  }

  const fullyInheriting = siteStatuses.filter((s) => s.siteOverrideCount === 0);
  const withOverrides = siteStatuses.filter((s) => s.siteOverrideCount > 0);

  return (
    <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-foreground">Site inheritance</h2>
        <span className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
          {siteStatuses.length} site{siteStatuses.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="mt-4 flex gap-3">
        <div className="flex-1 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-center">
          <p className="text-2xl font-bold text-primary">{fullyInheriting.length}</p>
          <p className="text-xs font-medium text-muted-foreground">Inheriting brand</p>
        </div>
        <div className="flex-1 rounded-2xl border border-border bg-background px-4 py-3 text-center">
          <p className="text-2xl font-bold text-foreground">{withOverrides.length}</p>
          <p className="text-xs font-medium text-muted-foreground">With overrides</p>
        </div>
      </div>

      <ul className="mt-4 space-y-2">
        {siteStatuses.map((status) => (
          <li
            key={status.site.id}
            className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-background px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">
                {status.site.name || status.site.slug}
              </p>
              {status.siteOverrideCount > 0 ? (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Overriding: {status.overriddenFields.join(", ")}
                </p>
              ) : (
                <p className="mt-0.5 text-xs text-primary">
                  Fully inheriting brand defaults
                </p>
              )}
            </div>
            <Link
              to={`/pages/site-settings?site=${status.site.id}`}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground"
            >
              Review
              <ArrowRight className="h-3 w-3" />
            </Link>
          </li>
        ))}
      </ul>

      <p className="mt-4 text-xs leading-5 text-muted-foreground">
        Sites that fully inherit will automatically reflect brand changes.
        Sites with overrides keep their explicit values until you refresh from brand defaults
        in their site settings.
      </p>
    </section>
  );
}

function resolvePreviewTheme(theme: SiteTheme): SiteTheme {
  return normalizeSiteTheme({
    presetId: theme.presetId ?? DEFAULT_THEME_BASELINE.presetId ?? "modern",
    ...theme,
  });
}

function buildPreviewStyle(theme: SiteTheme): CSSProperties {
  const resolvedTheme = resolvePreviewTheme(theme);
  return {
    "--preview-brand": resolvedTheme.colors?.brand ?? DEFAULT_COLORS.primary,
    "--preview-accent": resolvedTheme.colors?.accent ?? DEFAULT_COLORS.accent,
    "--preview-bg": resolvedTheme.colors?.background ?? DEFAULT_COLORS.background,
    "--preview-surface": resolvedTheme.colors?.surface ?? DEFAULT_PREVIEW_SURFACE,
    "--preview-text": resolvedTheme.colors?.text ?? DEFAULT_COLORS.text,
    "--preview-border": resolvedTheme.colors?.muted ?? DEFAULT_PREVIEW_MUTED,
    "--preview-radius": resolvedTheme.borderRadius ?? DEFAULT_PREVIEW_RADIUS,
  } as CSSProperties;
}

function buildPreviewButtonStyle(theme: SiteTheme, isPrimary: boolean): CSSProperties {
  const resolvedTheme = resolvePreviewTheme(theme);
  const radius = resolvedTheme.borderRadius ?? "9999px";
  const brandColor = resolvedTheme.colors?.brand ?? DEFAULT_COLORS.primary;
  const surfaceColor = resolvedTheme.colors?.surface ?? DEFAULT_PREVIEW_SURFACE;
  const textColor = resolvedTheme.colors?.text ?? DEFAULT_COLORS.text;
  const borderColor = resolvedTheme.colors?.muted ?? DEFAULT_PREVIEW_MUTED;

  if (isPrimary) {
    return {
      borderRadius: radius,
      border: "1px solid transparent",
      background: brandColor,
      color: "#FFFFFF",
      boxShadow: "0 12px 24px rgba(15,23,42,0.12)",
    };
  }

  return {
    borderRadius: radius,
    border: `1px solid ${borderColor}`,
    background: surfaceColor,
    color: textColor,
    boxShadow: "none",
  };
}

function buildPreviewCardStyle(theme: SiteTheme): CSSProperties {
  const resolvedTheme = resolvePreviewTheme(theme);

  return {
    borderRadius: resolvedTheme.borderRadius ?? DEFAULT_PREVIEW_RADIUS,
    background: resolvedTheme.colors?.surface ?? DEFAULT_PREVIEW_SURFACE,
    border: `1px solid ${resolvedTheme.colors?.muted ?? DEFAULT_PREVIEW_MUTED}`,
    boxShadow: "0 12px 24px rgba(15,23,42,0.08)",
  };
}

function buildPreviewFont(fontFamily: string | undefined): string {
  return fontFamily ? `'${fontFamily}', sans-serif` : "system-ui, sans-serif";
}

// ---------------------------------------------------------------------------
// ContrastAdvisory — non-blocking WCAG-AA hint shown beside a color input.
// Renders nothing when the pair is unparseable (e.g. mid-edit `var(--token)`)
// to avoid noisy feedback. Visual hint only — never blocks save.
// ---------------------------------------------------------------------------
function ContrastAdvisory({
  swatch,
  background,
  label,
}: {
  swatch: string;
  background: string;
  label: string;
}) {
  // The background swatch compared against itself is trivially 1:1 — skip.
  if (label === "background") {
    return null;
  }

  const ratio = contrastRatio(swatch, background);
  if (ratio === null) {
    return null;
  }
  const level: WcagLevel = classifyContrast(ratio);
  const passes = ratio >= WCAG_THRESHOLDS.AA_NORMAL;
  const formatted = ratio.toFixed(2);

  if (passes) {
    return (
      <p
        className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
        data-testid={`contrast-advisory-${label}`}
      >
        {formatted}:1 · {level}
      </p>
    );
  }

  const message =
    level === "AA-large"
      ? `${formatted}:1 — passes for large text only`
      : `${formatted}:1 — below WCAG AA (needs 4.5:1)`;

  return (
    <p
      role="status"
      data-testid={`contrast-advisory-${label}`}
      className="rounded-md bg-amber-50 px-2 py-1 text-[10px] font-medium leading-tight text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
    >
      {message}
    </p>
  );
}
